import { HTMLAttributes, ReactNode, useState } from 'react';

import { CloseOutlined, LockOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { Link } from 'react-router-dom';

import { ROLES } from '../../../../../helpers/utils/auth';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import styles from '../styles/AdMenu.module.scss';
import { TabType } from '../types';

type Item = {
  key: string;
  label: ReactNode;
  url?: string;
  withoutClose?: boolean;
  onClick?: () => void;
  style?: HTMLAttributes<HTMLDivElement>['style'];
  permission?: string[];
  isHelper?: boolean;
  // Принудительная блокировка пункта (например, недоступно на тарифе)
  locked?: boolean;
  // Кастомный текст в модалке для заблокированного пункта
  lockedMessage?: ReactNode;
};

export type DynamicMenuProps = {
  isOpen: boolean;
  onClose: () => void;
  activeTab: TabType;
  items?: Array<Item>;
  collapsed: boolean;
  nextLevel?: Omit<
    DynamicMenuProps,
    | 'onClose'
    | 'activeMainTab'
    | 'activeTab'
    | 'isOpen'
    | 'collapsed'
    | 'setActiveTab'
  >;
  level?: number;
};

const MenuItem: React.FC<{
  item: Item;
  activeTab: TabType;
  onClose: () => void;
}> = ({ item, activeTab, onClose }) => {
  const role = useTypedSelector((state) => state.currentUser.role);
  const havenPermisson = !item.permission || role === ROLES.super
    ? true
    : item.permission.includes(role);
  // Пункт заблокирован, если нет прав ИЛИ выставлен явный флаг locked (например, тариф)
  const isLocked = !havenPermisson || !!item.locked;
  const [ open, setOpen ] = useState(false);

  if (item.isHelper) {
    return (
      <div
        className={styles['menu-helper']}
        onClick={() => {
          item.onClick?.();
          onClose();
        }}
      >
        {item.label}
      </div>
    );
  }

  const classNames = [
    styles['menu-item'],
    activeTab === item.key ? styles['active'] : '',
    isLocked ? styles['menu-item-disabled'] : '',
    item.withoutClose ? styles['has-submenu'] : '',
  ].filter(Boolean).join(' ');

  const handleClick = () => {
    item.onClick?.();
    !item.withoutClose && onClose();
  };

  if (item.onClick) {
    return (
      <div className={classNames} onClick={handleClick} style={item.style}>
        {item.label}
      </div>
    );
  }

  return !isLocked ? (
    <Link to={item.url || '#'} className={classNames} onClick={handleClick}>
      {item.label}
    </Link>
  ) : (
    <>
      <div
        className={classNames}
        style={item.style}
        onClick={() => setOpen(true)}
      >
        {item.label} <LockOutlined style={{ marginLeft: 8, fontSize: 12 }} />
      </div>
      <Modal
        open={open}
        onOk={() => setOpen(false)}
        okText='Ок'
        cancelText
        centered
        closable
        maskClosable
        destroyOnClose
        closeIcon={<CloseOutlined onClick={() => setOpen(false)}/>}
      >
        {/* Если прав нет — сообщение про доступ, иначе кастомный текст блокировки (тариф) */}
        {!havenPermisson ? (
          <>
            К выбранному разделу у Вас доступ закрыт.
            <br />
            Обратитесь к руководству, чтобы Вам расширили права.
          </>
        ) : (
          item.lockedMessage
        )}
      </Modal>
    </>
  );
};

const DynamicMenu: React.FC<DynamicMenuProps> = ({
  isOpen,
  onClose,
  activeTab,
  items,
  collapsed,
  nextLevel,
  level = 1,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className={`${styles['side-menu']} ${
        level === 1 ? styles['side-menu-lvl-1'] : ''
      } ${collapsed ? styles['side-menu-collapsed'] : ''}`}
    >
      <div className={styles['side-menu-content']}>
        {items && (
          <div className={styles['menu-section']}>
            {items.map((item) => (
              <MenuItem
                key={item.key}
                item={item}
                activeTab={activeTab}
                onClose={onClose}
              />
            ))}
          </div>
        )}
      </div>
      {nextLevel && (
        <DynamicMenu
          {...nextLevel}
          level={level + 1}
          isOpen={isOpen}
          onClose={onClose}
          activeTab={activeTab}
          collapsed={collapsed}
        />
      )}
      <div className={styles['side-menu-overlay']} onClick={onClose} />
    </div>
  );
};

export default DynamicMenu;
